export function treeWalker(node, id) {
  // Walks the DB version of the tree looking for a node with an _id of 'id'
  // Returns either the node or null if it can't find it.
  if (node._id === id) {
    return node;
  } else {
    if (node.children.length) {
      let result;
      for (const item of node.children) {
        result = treeWalker(item, id);
        if (result) {
          break;
        }
      }
      return result;
    } else {
      return null;
    }
  }
}

export function treePeriodicAdder(node, timestamp) {
  // Walks the DB version of the tree and adds a new value to any
  // record marked as periodic. It will add the appropriate numbers
  // of entries based on the metadata.
  const now = new Date().toISOString();

  if (node.meta.periodic) {
    // If this node uses arrays of more than one element, create the proper length array

    const newValueLen = node.meta.value_name ? node.meta.value_name.length : 1;
    const newValue = {
      event: "",
      eventTimestamp: timestamp.toISOString(),
      timestamp: now,
      val: new Array(newValueLen)
    };
    if (newValueLen == 1) {
      newValue.val = [];
    }
    if (node.meta.min) {
      newValue.val.fill(node.meta.min);
    }
    node.value.push(newValue);
  }
  if (node.children.length) {
    for (const item of node.children) {
      treePeriodicAdder(item, timestamp);
    }
  }
}
export function treePeriodicAdderForEventNodes(node, timestamp, eventkey) {
  // find the nodes that are part of the event
  // determine whether the new data is part of an existing timestamp or a new one
  const now = new Date().toISOString();
  if (node.meta.events?.length) {
    if (node.meta.periodic && node.meta.events.includes(eventkey)) {
      const newValueLen = node.meta.value_name
        ? node.meta.value_name.length
        : 1;
      const newValue = {
        event: "",
        eventTimestamp: timestamp,
        timestamp: now,
        val: new Array(newValueLen)
      };
      if (newValueLen == 1) {
        newValue.val = [];
      }
      if (node.meta.min) {
        newValue.val.fill(node.meta.min);
      }

      node.value.push(newValue);
    }
  }

  if (node.children.length) {
    for (const item of node.children) {
      treePeriodicAdderForEventNodes(item, timestamp, eventkey);
    }
  }
}

import isArray from "lodash/isArray";
import cloneDeep from "lodash/cloneDeep";

export function treePeriodicConverter(node, timestamp) {
  // Walks the DB version of the tree and converts nodes to
  // be periodic. It will also update the value field to be an array
  // of objects
  const now = new Date().toISOString();
  const granularity = 15; // For event timestamps, use 15 minute granularity

  let newNode = cloneDeep(node);

  if (node.meta.type !== "title" && node.value !== undefined) {
    if (node.meta.periodic) {
      // Node is already periodic. Make sure val is an array and fix timestamps
      // Check if value is an array
      let newValue = [];
      if (Array.isArray(node.value)) {
        newValue = node.value
          .map(item => {
            if (!Array.isArray(item.val)) {
              item.val = [item.val];
            }
            if (item.timestamp !== new Date(item.timestamp).toISOString()) {
              item.timestamp = new Date(item.timestamp).toISOString();
            }
            if (!item.eventTimestamp) {
              const datetime = new Date(item.timestamp);
              datetime.setMilliseconds(0);
              datetime.setSeconds(0);
              datetime.setMinutes(
                datetime.getMinutes() - (datetime.getMinutes() % granularity)
              );
              item.eventTimestamp = datetime.toISOString();
            }
            return item;
          })
          .filter(item => item.val.some(v => v !== null && v !== ""));
      } else {
        newValue = [
          {
            event: "",
            timestamp: new Date(`${timestamp}Z`).toISOString(),
            val: isArray(node.value) ? node.value : [node.value]
          }
        ];
        const item = node.data?.value;
        if (!item?.eventTimestamp) {
          const datetime = new Date(timestamp);
          datetime.setMilliseconds(0);
          datetime.setSeconds(0);
          datetime.setMinutes(
            datetime.getMinutes() - (datetime.getMinutes() % granularity)
          );
          newValue[0].eventTimestamp = datetime.toISOString();
        }
      }
      // Fix types with spaces in them
      newNode.meta.type.replace(" ", "-");
      newNode.value = newValue.concat();

      // Convert groups from a string to an array
      if (
        newNode.meta.periodic_group &&
        !Array.isArray(newNode.meta.periodic_group)
      ) {
        newNode.meta.periodic_group = [newNode.meta.periodic_group];
      }
    }
  }
  newNode.children.forEach((child, index) => {
    newNode.children[index] = treePeriodicConverter(child, timestamp);
  });

  return newNode;
}

export function treeBuildGroups(groupList, node) {
  // Walks the DB version of the tree and sorts node ids into groups

  let groups = node.meta.periodic_group;
  if (groups) {
    if (!Array.isArray(node.meta.periodic_group)) {
      groups = [groups];
    }

    groups.forEach(group => {
      if (!Object.prototype.hasOwnProperty.call(groupList, group)) {
        groupList[group] = new Array(node);
      } else {
        if (groupList[group].indexOf(node) === -1) {
          groupList[group].push(node);
        }
      }
    });
  }
  if (node.children) {
    node.children.forEach(child => {
      groupList = treeBuildGroups(groupList, child);
    });
  }
  // console.log(node.data, groupList);
  return groupList;
}

export function treeBuildEvents(eventList, node) {
  // Walks the DB version of the tree and sorts node ids into groups

  const events = node.meta.events;
  if (events) {
    events.forEach(event => {
      if (!Object.prototype.hasOwnProperty.call(eventList, event)) {
        eventList[event] = new Array(node);
      } else {
        if (eventList[event].indexOf(node) === -1) {
          eventList[event].push(node);
        }
      }
    });
  }
  if (node.children) {
    node.children.forEach(child => {
      eventList = treeBuildEvents(eventList, child);
    });
  }

  // console.log(node.data, eventList);
  return eventList;
}

export function treeBuildCatList(catList, node) {
  // Walks the DB version of the tree and builds a list of all categories with their ids

  if (node.meta.type !== "title") {
    // Only include non-title nodes
    catList.push({ id: node._id, name: node.name });
  }
  node.children?.forEach(child => {
    catList = treeBuildCatList(catList, child);
  });

  // console.log(node.data, catList);
  return catList;
}
